// SimpleFIN /accounts JSON loader. Mirrors scripts/loaders.py::load_simplefin_json.
//
// The doc shape (one file per /accounts call against the user's
// SimpleFIN access URL):
//   {
//     "accounts": [
//       {
//         "id": "...",
//         "name": "BMO Chequing",
//         "currency": "CAD",
//         "org": {"name": "BMO", "domain": "bmo.com"},
//         "transactions": [
//           {"id": ..., "posted": <unix>, "amount": "12.34",
//            "description": "...", "payee": "...", "memo": "...",
//            "pending": false},
//           ...
//         ]
//       },
//       ...
//     ]
//   }
//
// Sign convention: SimpleFIN ships positive = credit, negative = debit.
// We FLIP so our positive = expense. Asserted by Python tests.

import {
  type Transaction,
  newTransaction,
  makeId,
  normalizeVendor,
  isInterPortalTransfer,
  extractLast4,
} from "../common.js";

interface SimpleFinTx {
  id?: string;
  posted?: number | string;
  amount?: number | string;
  description?: string;
  payee?: string;
  memo?: string;
  pending?: boolean;
}

interface SimpleFinAccount {
  id?: string;
  name?: string;
  currency?: string;
  org?: { name?: string; domain?: string };
  transactions?: SimpleFinTx[];
  /** Non-standard web-app override. Injected by `applyAccountOverrides`
   *  in simplefin_storage.ts when the user has set an explicit kind for
   *  this account. Absent on raw SimpleFIN responses; the loader treats
   *  absence as the default "debit". */
  _pf_account_kind?: "debit" | "liability";
}

interface SimpleFinDoc {
  accounts?: SimpleFinAccount[];
}

function simplefinAccountLabel(acct: SimpleFinAccount): string {
  const n = (acct.name ?? "").trim() || "Account";
  const currency = (acct.currency ?? "").trim();
  if (currency && !n.toUpperCase().includes(currency.toUpperCase())) {
    return `${n} (${currency})`;
  }
  return n;
}

async function simplefinTxToTransaction(
  tx: SimpleFinTx,
  accountLabel: string,
  accountLast4: string,
  institutionName: string,
  sourceFile: string,
  accountKind: "debit" | "liability" = "debit",
): Promise<Transaction | null> {
  const postedRaw = tx.posted;
  const posted = typeof postedRaw === "number"
    ? postedRaw
    : Number(typeof postedRaw === "string" ? postedRaw.trim() : NaN);
  if (!Number.isFinite(posted)) return null;
  // SimpleFIN's posted is a Unix timestamp (seconds). Convert to UTC date.
  const date = new Date(posted * 1000).toISOString().slice(0, 10);

  const rawAmount = tx.amount;
  const amountNum = typeof rawAmount === "number"
    ? rawAmount
    : Number(typeof rawAmount === "string" ? rawAmount.trim() : NaN);
  if (!Number.isFinite(amountNum)) return null;
  // SimpleFIN +ve = credit (income), -ve = debit (expense). Flip.
  const amount = -amountNum;

  const description =
    (tx.description ?? "").trim() ||
    (tx.payee ?? "").trim() ||
    (tx.memo ?? "").trim();
  if (!description || isInterPortalTransfer(description)) return null;

  const id = await makeId("simplefin", accountLabel, date, amount, description);
  return newTransaction({
    id,
    date,
    vendor_raw: description,
    vendor_key: normalizeVendor(description),
    amount: Math.round(amount * 100) / 100,
    portal: "simplefin",
    account: accountLabel,
    account_kind: accountKind,
    account_last4: accountLast4,
    source_file: sourceFile,
    raw: {
      simplefin_transaction_id: tx.id ?? "",
      memo: tx.memo ?? "",
      pending: tx.pending ?? false,
    },
    institution_name: institutionName,
  });
}

export async function loadSimplefinJson(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  let doc: SimpleFinDoc;
  try {
    doc = JSON.parse(text);
  } catch (err) {
    throw new Error(`SimpleFIN JSON parse error in ${filename}: ${(err as Error).message}`);
  }
  const out: Transaction[] = [];
  for (const acct of doc.accounts ?? []) {
    const accountLabel = simplefinAccountLabel(acct);
    // SimpleFIN has no standard mask field. Best-effort: take last 4 digits
    // from id, fall back to last 4 chars of name. extractLast4 returns ""
    // if neither yields ≥4 digits.
    let last4 = extractLast4(acct.id ?? "");
    if (!last4) last4 = extractLast4(acct.name ?? "");
    const institutionName =
      ((acct.org?.name ?? acct.org?.domain) ?? "").trim();
    const accountKind = acct._pf_account_kind ?? "debit";
    for (const tx of acct.transactions ?? []) {
      const t = await simplefinTxToTransaction(
        tx, accountLabel, last4, institutionName, filename, accountKind,
      );
      if (t) out.push(t);
    }
  }
  return out;
}

export const _internal = { simplefinAccountLabel, simplefinTxToTransaction };
